import * as React from "react";
import { useEffect, useMemo } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { ContainerType } from "../../types/ContainerType";
import { InputTextField } from "../FormFields/RenderInputTextField";
import { NumberField } from "../FormFields/RenderNumberField";
import {
  createContainerType,
  updateContainerType,
} from "../../data/containertype";
import { useDispatch, useSelector } from "react-redux";
import { hideModal, hideChildModal } from "../../data/modal";

import { Button, Grid } from "@mui/material";

import { Close as CloseIcon, Save as SaveIcon } from "@mui/icons-material";
import AliasFields from "./Aliases";

interface ContainerTypeFormProps {
  containerType?: any;
  callback?: any;
  initialContainerType?: any;
  containerTypeDefaults?: any;
  mode?: "create" | "edit";
}

const normalizeContainerTypeDefaults = (value: any) => {
  const fallback = new ContainerType();

  return {
    ...fallback,
    ...(value ?? {}),
    name: value?.name ?? "",
    abbreviation: value?.abbreviation ?? value?.name ?? "",
    teu: value?.teu ?? null,
    height: value?.height ?? null,
    width: value?.width ?? null,
    emptyWeight: value?.emptyWeight ?? null,
    aliases: Array.isArray(value?.aliases) ? value.aliases : [],
  };
};

const ContainerTypeForm = ({
  containerType,
  callback,
  initialContainerType,
  containerTypeDefaults,
}: ContainerTypeFormProps) => {
  const dispatch = useDispatch();

  const selectedData = useSelector((state: any) => ({
    modal: state.modal,
  }));

  const defaultValues = useMemo(() => {
    if (containerType) {
      return normalizeContainerTypeDefaults(containerType);
    }

    return normalizeContainerTypeDefaults(
      containerTypeDefaults || initialContainerType
    );
  }, [containerType, containerTypeDefaults, initialContainerType]);

  const {
    handleSubmit,
    control,
    formState,
    register,
    reset,
  } = useForm<any>({
    defaultValues,
  });

  const { isSubmitting } = formState;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleCloseForm = () => {
    if (selectedData.modal.childModal) {
      dispatch(hideChildModal() as any);
      return;
    }

    dispatch(hideModal() as any);
  };

  const onSubmit: SubmitHandler<ContainerType> = async (data) => {
    if (containerType) {
      await dispatch(updateContainerType(data) as any);

      if (typeof callback === "function" && callback.length <= 1) {
        await callback({
          ...containerType,
          ...data,
        });
      }

      handleCloseForm();
      return;
    }

    await dispatch(createContainerType(data, callback) as any);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container xs={12}>
        <Grid item xs={12} sm={12}>
          <Grid item xs={12} className="form-fields-container">
            <Grid item xs={12} sm={2}>
              <InputTextField
                name="name"
                label="Name"
                control={control}
                required
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <InputTextField
                name="abbreviation"
                label="Abbreviation"
                control={control}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <NumberField name="teu" label="TEU" control={control} />
            </Grid>

            <Grid item xs={12} sm={2}>
              <NumberField name="height" label="Height (m)" control={control} />
            </Grid>

            <Grid item xs={12} sm={2}>
              <NumberField name="width" label="Width (m)" control={control} />
            </Grid>

            <Grid item xs={12} sm={2}>
              <NumberField
                name="emptyWeight"
                label="Weight (kg)"
                control={control}
              />
            </Grid>
          </Grid>

          <Grid item xs={12} className="form-fields-container">
            <Grid item xs={12} sm={12}>
              <AliasFields control={control} register={register} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} className="button-wrapper">
          <Button
            type="button"
            onClick={handleCloseForm}
            disabled={isSubmitting}
            variant="contained"
          >
            <CloseIcon fontSize="inherit" />
            &nbsp;Close
          </Button>

          <Button type="submit" disabled={isSubmitting} variant="contained">
            <SaveIcon fontSize="inherit" />
            &nbsp;Save
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ContainerTypeForm;